import { Component } from '@angular/core';
import { InventoryService } from '@inventory/services/inventory.service';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { ExternalVendorState } from '@usf/ngrx-product';
import { NonUSFProduct } from '@usf/product-types';
import { firstValueFrom, Observable } from 'rxjs';
import { first, take } from 'rxjs/operators';
import {
  manageNonUsfProductsViewModel,
  NonUSFProductViewModel,
} from './models/non-usf-products-view-model';
import { NonUsfProductViewModelSelectors } from './selectors/non-usf-selectors';
import { ManageNonUsfProductsService } from './services/manage-non-usf-products.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { transformNonUsfProductToProduct } from './transformers/non-usf-products-transformers';
import { FEATURES } from '@shared/constants/splitio-features';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-manage-non-usf-products',
  templateUrl: './manage-non-usf-products.page.html',
  styleUrls: ['./manage-non-usf-products.page.scss'],
})
export class ManageNonUsfProductsPage implements ViewWillEnter, ViewWillLeave {
  breadcrumbs: Crumb[];
  showBackButton = false;
  nonUsfProductViewModel$: Observable<manageNonUsfProductsViewModel>;
  totalSelected$: Observable<number>;
  addNonUsfListFlag$: Observable<boolean>;

  searchKey = '';

  platformEnum = PlatformEnum;
  pimFlag = false;

  constructor(
    readonly panAppState: PanAppState,
    public manageNonUsfProductsService: ManageNonUsfProductsService,
    public inventoryService: InventoryService,
    private translateService: TranslateService,
    readonly platformService: PlatformService,
    private nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    public addToListModalService: AddToListModalService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.setNavigationElements();
  }

  private setNavigationElements(): void {
    const mppSource = this.route.snapshot.queryParamMap.get('mppSource');

    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    const fromModal = navigationState?.['fromModal'] || false;

    if (fromModal) {
      this.showBackButton = true;
    } else {
      this.showBackButton = false;
      this.setBreadcrumbs(mppSource);
    }
  }

  private setBreadcrumbs(mppSource: string | null): void {
    let homeCrumbUrl = this.manageNonUsfProductsService.isTouch()
      ? '/touch/home'
      : '/desktop/home';

    const fromMPP = !!mppSource;

    let breadcrumbLabel = 'i18n.breadcrumbs.inventory';
    let breadcrumbUrl = '/' + PATHS.INVENTORY;

    if (fromMPP) {
      breadcrumbUrl = '/' + PATHS.MPP;

      switch (mppSource) {
        case 'Recipes':
          breadcrumbLabel = 'i18n.breadcrumbs.mppRecipes';
          breadcrumbUrl = '/' + PATHS.MPP + '/' + PATHS.RECIPES;
          break;
        case 'Dashboard':
          breadcrumbLabel = 'i18n.breadcrumbs.mpp';
          break;
        default:
          breadcrumbLabel = 'i18n.breadcrumbs.mpp';
          break;
      }
    }

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: breadcrumbLabel,
        url: breadcrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.nonUSF',
        url: '',
        active: true,
      },
    ];
  }

  ionViewWillLeave(): void {
    this.manageNonUsfProductsService.resetSelections();
    this.manageNonUsfProductsService.resetNonUsfSort();
    this.manageNonUsfProductsService.clearImportErrors();
  }

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.manageNonUsfProducts),
    );
    this.setViewModel();
    this.resetSearch();
    this.resetSearchKey();
    this.setPimFlag();
    this.manageNonUsfProductsService.trackManageNonUsfProducts();

    // Initialize feature flag observable
    this.addNonUsfListFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_add_non_usf_list.name,
    ]);
  }

  setPimFlag = async () => {
    this.pimFlag = await firstValueFrom(
      this.manageNonUsfProductsService.pimFeatureFlag$,
    );
  };

  setViewModel = () => {
    this.nonUsfProductViewModel$ =
      this.nonUsfProductViewModelSelectors.manageNonUSFProductViewModel$;
    this.totalSelected$ = this.manageNonUsfProductsService.getTotalSelected$();
  };

  handleProductDismiss = () => {
    this.manageNonUsfProductsService.openOrCloseNonUsfProductModal(false);
  };

  handleDeleteDismiss = () => {
    this.manageNonUsfProductsService.openOrCloseDeleteProductModal(false);
  };

  handleDeleteSubmit = () => {
    this.manageNonUsfProductsService.deleteSelections();
    this.manageNonUsfProductsService.openOrCloseDeleteProductModal(false);
  };

  handleSelection = (event: NonUSFProductViewModel) => {
    this.manageNonUsfProductsService.handleCardSelection(event);
  };

  handleDismissToast = () => {
    this.manageNonUsfProductsService.showImportNonUsfProductSuccessToast(false);
  };

  handleNameClick = (
    product: NonUSFProductViewModel,
    vendors: ExternalVendorState[],
  ) => {
    this.manageNonUsfProductsService.handleOpenRequestForCardClick(
      product,
      vendors,
    );
  };

  handleDeselectAllProducts() {
    this.manageNonUsfProductsService.resetSelections();
  }

  handleSelectAllProducts(products: NonUSFProduct[]) {
    this.manageNonUsfProductsService.selectAll(products);
  }

  handleAddToInventory() {
    this.manageNonUsfProductsService
      .getSelections$()
      .pipe(first())
      .subscribe(selections => {
        const inventoryId = this.inventoryService.exposedLastInventory;
        this.inventoryService
          .selectInventoryById$(inventoryId)
          .pipe(take(1))
          .subscribe(async inventory => {
            this.inventoryService.openAddToInventoryModal(
              selections,
              this.manageNonUsfProductsService.platformService.isTouch.value,
              this.manageNonUsfProductsService.platformService.platformType,
              this.inventoryService.addNonUSFProdFromInventoryWorksheet
                ? inventory
                : undefined,
              true,
            );
          });
      });
  }

  handleDelete() {
    this.manageNonUsfProductsService.openOrCloseDeleteProductModal(true);
  }

  handleShowAddToInventory() {
    firstValueFrom(this.nonUsfProductViewModel$).then(viewModel => {
      if (viewModel) {
        this.manageNonUsfProductsService.handleShowAddToInventory(() => {
          this.handleAddToList(viewModel);
        });
      }
    });
  }

  resetSearchKey() {
    if (this.searchKey) {
      this.searchKey = '';
    }
  }

  async resetSearch() {
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  search() {
    this.manageNonUsfProductsService.searchNonUsfWorksheet(this.searchKey);
  }

  handleSortClick(headerName: string) {
    this.manageNonUsfProductsService.setNonUsfSort(headerName);
  }

  async openCreateNonUsfProductsModal(
    viewModel: manageNonUsfProductsViewModel,
  ) {
    this.manageNonUsfProductsService.openCreateNonUsfProductsOptions(viewModel);
  }

  openDownloadOverlay(viewModel: manageNonUsfProductsViewModel) {
    this.manageNonUsfProductsService.openDownloadOverlay(viewModel);
  }

  openNonUsfProductErrorsModal() {
    this.manageNonUsfProductsService.handleOpenImportUsfProductErrorsModal();
  }

  closeImportErrorsModal() {
    this.manageNonUsfProductsService.openOrCloseNonUsfImportErrorsModal(false);
  }

  downloadImportErrors(event) {
    this.manageNonUsfProductsService.openOrCloseNonUsfImportErrorsModal(false);
    this.manageNonUsfProductsService.downloadImportErrors(event);
  }

  async handleAddToList(viewModel: manageNonUsfProductsViewModel) {
    const isFeatureEnabled = await firstValueFrom(this.addNonUsfListFlag$);

    if (!isFeatureEnabled) {
      return;
    }

    const selectedProducts = viewModel.products.filter(
      product => product.isSelected,
    );

    const transformedProducts = selectedProducts.map(product =>
      transformNonUsfProductToProduct(product),
    );

    this.addToListModalService.openModalForNonUsf(transformedProducts);
  }
}
